import React, { useState } from 'react';
import { getSettings } from '../services/settings';

const PaymentModal = ({ orderAmount, onPaymentSubmit, onClose }) => {
  const [paymentMethod, setPaymentMethod] = useState('bkash');
  const [transactionId, setTransactionId] = useState('');
  const [senderNumber, setSenderNumber] = useState('');
  const [settings, setSettings] = useState({ 
    bkash_number: '+8801712345678', 
    nagad_number: '+8801718765432',
    bkash_fee: '1.85',
    nagad_fee: '1.50',
    delivery_charge: '50'
  });
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (data) {
          setSettings({
            bkash_number: data.bkash_number || settings.bkash_number,
            nagad_number: data.nagad_number || settings.nagad_number,
            bkash_fee: data.bkash_fee || settings.bkash_fee,
            nagad_fee: data.nagad_fee || settings.nagad_fee,
            delivery_charge: data.delivery_charge || settings.delivery_charge
          });
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Calculate fees and total
  const subtotal = parseFloat(orderAmount) || 0;
  const deliveryCharge = parseFloat(settings.delivery_charge) || 0;
  
  // Calculate transaction fee based on payment method
  const calculateTransactionFee = () => {
    if (paymentMethod === 'bkash') {
      const bkashFeePercent = parseFloat(settings.bkash_fee) || 0;
      return (subtotal * bkashFeePercent) / 100;
    } else if (paymentMethod === 'nagad') {
      const nagadFeePercent = parseFloat(settings.nagad_fee) || 0;
      return (subtotal * nagadFeePercent) / 100;
    }
    return 0; // No fee for cash on delivery
  };
  
  const transactionFee = calculateTransactionFee();
  const totalAmount = subtotal + transactionFee + deliveryCharge;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const paymentData = {
      payment_method: paymentMethod,
      transaction_id: paymentMethod !== 'cash' ? transactionId : '',
      sender_number: paymentMethod !== 'cash' ? senderNumber : '',
      delivery_charge: deliveryCharge,
      transaction_fee: transactionFee,
      total_amount: totalAmount
    };
    
    onPaymentSubmit(paymentData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 modal-backdrop">
      <div 
        className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] flex flex-col my-8"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 bengali-text">
            পেমেন্ট
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Amount Breakdown */}
        <div className="bg-gray-50 border rounded-lg p-4 mb-6 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 bengali-text">পণ্যের মূল্য:</span>
            <span className="font-medium bengali-text">৳{subtotal.toFixed(2)}</span>
          </div>
          {transactionFee > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 bengali-text">
                ট্রানজেকশন ফি ({paymentMethod === 'bkash' ? 'bKash' : 'Nagad'}: {paymentMethod === 'bkash' ? settings.bkash_fee : settings.nagad_fee}%):
              </span>
              <span className="font-medium bengali-text">৳{transactionFee.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 bengali-text">ডেলিভারি চার্জ:</span>
            <span className="font-medium bengali-text">৳{deliveryCharge.toFixed(2)}</span>
          </div>
          <div className="border-t pt-2 mt-2 flex justify-between">
            <span className="text-lg font-semibold text-gray-900 bengali-text">মোট পরিমাণ:</span>
            <span className="text-2xl font-bold text-orange-600 bengali-text">৳{totalAmount.toFixed(2)}</span>
          </div>
        </div>

        {/* Payment Method Selection */}
        <form id="payment-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3 bengali-text">
              পেমেন্ট মাধ্যম নির্বাচন করুন *
            </label>
            
            {/* bKash Personal */}
            <div className="mb-3">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="bkash"
                  checked={paymentMethod === 'bkash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 bengali-text">bKash Personal</div>
                  <div className="text-sm text-gray-600 bengali-text">সেন্ড মানি করুন: {settings.bkash_number}</div>
                </div>
              </label>
            </div>

            {/* Nagad Personal */}
            <div className="mb-3">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="nagad"
                  checked={paymentMethod === 'nagad'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 bengali-text">Nagad Personal</div>
                  <div className="text-sm text-gray-600 bengali-text">সেন্ড মানি করুন: {settings.nagad_number}</div>
                </div>
              </label>
            </div>

            {/* Cash on Delivery - moved to bottom */}
            <div className="mb-3">
              <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="mr-3"
                />
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 bengali-text">ক্যাশ অন ডেলিভারি</div>
                  <div className="text-sm text-gray-600 bengali-text">পেমেন্ট পদ্ধতি নীচে নির্বাচন করুন</div>
                </div>
              </label>
            </div>
          </div>

          {/* Transaction Details (for bKash/Nagad) */}
          {paymentMethod !== 'cash' && (
            <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              {/* Payment Instructions */}
              <div className="bg-white p-3 rounded-lg border border-blue-300">
                <h4 className="font-semibold text-gray-900 mb-2 bengali-text">
                  💡 {paymentMethod === 'bkash' ? 'bKash' : 'Nagad'} দিয়ে পেমেন্ট করুন:
                </h4>
                <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 bengali-text">
                  <li>{paymentMethod === 'bkash' ? 'bKash' : 'Nagad'} অ্যাপ খুলুন</li>
                  <li>Send Money সিলেক্ট করুন</li>
                  <li>এই নাম্বারে মানি সেন্ড করুন: <span className="font-semibold text-orange-600">{paymentMethod === 'bkash' ? settings.bkash_number : settings.nagad_number}</span></li>
                  <li>মোট পরিমাণ: <span className="font-bold text-orange-600">৳{totalAmount.toFixed(2)}</span></li>
                  <li>ট্রানজেকশন নম্বরটি Copy করুন</li>
                  <li>নিচের ফর্মে ট্রানজেকশন নম্বর প্রদান করুন</li>
                </ol>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
                  ট্রানজেকশন নম্বর *
                </label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent"
                  placeholder="ট্রানজেকশন নম্বর লিখুন"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 bengali-text">
                  আপনার মোবাইল নম্বর *
                </label>
                <input
                  type="tel"
                  value={senderNumber}
                  onChange={(e) => setSenderNumber(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-custom focus:border-transparent"
                  placeholder="০১XXXXXXXXX"
                />
              </div>
            </div>
          )}
        </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="border-t p-4 bg-gray-50 space-y-3">
          <button
            type="submit"
            form="payment-form"
            className="w-full px-6 py-4 bg-orange-custom hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors shadow-lg bengali-text"
          >
            <div className="text-lg">অর্ডার সম্পন্ন করুন</div>
            <div className="text-xl font-bold mt-1">৳{totalAmount.toFixed(2)} পেমেন্ট করুন</div>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors bengali-text font-medium"
          >
            বাতিল
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;

